import { useTheme } from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { Asset } from "@/Dashboard/pages/AssetLibrary/types";
import { ELEMENT_TYPE, EXPERIENCE_PRIMARY_FILTER, ROLES, TAB_STATUSES } from "@/constants";
import { useTabStatus, useUrlParams } from "@/hooks";
import { ExperienceService } from "@/services";
import { selectUser } from "@/store/auth/auth.slice";
import { useThunkSelector } from "@/store/hooks";
import {
  contentElementsSelector,
  currentExperienceSelector,
  currentMediaItemSelector,
  elementsSelector,
  experienceMediaItemsSelector,
  visualBuilderDataSelector,
} from "@/store/selectors";
import type { VisualBuilderDataState } from "@/store/types";

import * as S from "./styled";
import ExperienceTabs from "../ExperienceTabs";
import ExperienceToolbar from "../ExperienceToolbar/ExperienceToolbar";
import ExperienceTitle from "../Forms/ExperienceTitle";
import ContactDialog from "../Header/ContactDialog";
import ContactDialogV2 from "../Header/ContactDialogV2";

type TabMapStatus = "collect" | "customize" | "share";

const tabMap = {
  [TAB_STATUSES.COLLECT]: 0,
  [TAB_STATUSES.CUSTOMIZE]: 1,
  [TAB_STATUSES.SHARE]: 2,
};

interface ExperienceTabMenuProps {
  readonly belowSmallWidth: boolean;
  readonly label: string;
}

export default function ExperienceTabMenu(props: ExperienceTabMenuProps) {
  const { label = "", belowSmallWidth } = props;

  const params = useUrlParams();

  const {
    propertyId,
    experienceId = null,
    userId,
    tabStatus,
  } = params as {
    propertyId?: string;
    experienceId?: string;
    userId?: string;
    tabStatus?: TabMapStatus;
  };

  const { visualBuilderData: baseVisualBuilderData } = useSelector(visualBuilderDataSelector(params)) as {
    visualBuilderData: VisualBuilderDataState & {
      assetCategoryTab: VisualBuilderDataState;
      contentTypeTab: VisualBuilderDataState;
    };
  };

  const { experience } = useSelector(currentExperienceSelector);
  const { mediaItem: currentMediaItem } = useSelector(currentMediaItemSelector);
  const { experienceMediaItems = {} } = useSelector(experienceMediaItemsSelector(propertyId, experienceId));

  const { elements } = useSelector(elementsSelector(propertyId || "", experienceId || null));
  const { elements: contentElements } = useSelector(contentElementsSelector(propertyId, currentMediaItem.id));
  const user = useThunkSelector(selectUser);
  const { hasTabStatus, isTabPresent, isTabCollect, isTabCustomize, isPublicPageTab } = useTabStatus();

  const { primaryFilter = EXPERIENCE_PRIMARY_FILTER.TAG, assetCategoryTab, contentTypeTab } = baseVisualBuilderData;
  const tabVisualBuilderData = useMemo(
    () => (primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE ? contentTypeTab : assetCategoryTab),
    [primaryFilter, assetCategoryTab, contentTypeTab],
  );

  const publicVisualBuilderData = useMemo(
    () => ({
      ...baseVisualBuilderData,
      ...tabVisualBuilderData,
    }),
    [baseVisualBuilderData, tabVisualBuilderData],
  );

  const visualBuilderData = useMemo(
    () => (isPublicPageTab ? publicVisualBuilderData : baseVisualBuilderData),
    [isPublicPageTab, publicVisualBuilderData, baseVisualBuilderData],
  );

  const experienceMediaItemsLength = Object.keys(experienceMediaItems).length;

  let { leftDrawerAutoHide = false } = visualBuilderData;

  const { leftDrawer = true, gridView = false, locked, flags, createdBy = {} } = visualBuilderData;

  const currentDbcUser = experience.dbcUser ?? createdBy;

  const filteredMediaItems: Asset[] = visualBuilderData.filteredMediaItems || [];

  if (isTabCustomize) {
    leftDrawerAutoHide = false;
  }

  const isSalesPerson = !!user && user.userRole?.title === ROLES.SALESPERSON;
  const isTemplate = experience?.isTemplate;

  const rightElements = elements.filter((el: { type: string }) => (isTabCollect ? el.type !== ELEMENT_TYPE.MAP : el));

  const allElementData = isTabPresent ? contentElements : [...contentElements, ...rightElements];

  const { transitions } = useTheme(); // Access the Material-UI theme
  const appBarTransition = transitions.create(["margin", "width"], {
    easing: transitions.easing.sharp,
    duration: transitions.duration.leavingScreen,
  });

  const { title = "" } = experience || {};

  const adjustedTabStatus = tabStatus ? tabMap[tabStatus] || 0 : 0;

  const handleTitleUpdate = async (title: string) => {
    if (title && propertyId && experienceId) {
      await ExperienceService.updateExperience(Number(propertyId), Number(experienceId), {
        title,
      });
    }
  };

  return (
    <S.MenuHeaderContainer data-testid="experience-tab-menu-container">
      <S.MenuAppBar $appBarTransition={appBarTransition} $isPublicPageTab={isPublicPageTab} position="fixed">
        {!isTabPresent && !isPublicPageTab && (
          <S.MenuUpperToolbar $hasTabStatus={hasTabStatus}>
            <>
              <S.HeaderTitleSection>
                <ExperienceTitle
                  label={label}
                  handleTitleUpdate={handleTitleUpdate}
                  title={title}
                  disabled={locked || (isSalesPerson && isTemplate)}
                />
              </S.HeaderTitleSection>
              <S.HeaderMenuSection>
                <ExperienceTabs
                  propertyId={`${propertyId}`}
                  experienceId={`${experienceId}`}
                  userId={`${userId}`}
                  tabStatus={`${tabStatus}`}
                  adjustedTabStatus={adjustedTabStatus}
                  isBelowSmallWidth={belowSmallWidth}
                  experienceMediaItemsLength={experienceMediaItemsLength}
                />
              </S.HeaderMenuSection>
            </>
            <S.HeaderIconSection />
          </S.MenuUpperToolbar>
        )}

        <ExperienceToolbar
          allElementData={allElementData}
          gridView={gridView}
          leftDrawer={leftDrawer}
          leftDrawerAutoHide={leftDrawerAutoHide}
          filteredMediaItems={filteredMediaItems}
          locked={locked || (isSalesPerson && isTemplate)}
        />
      </S.MenuAppBar>
      {flags?.["digital-business-card"] ? (
        <ContactDialogV2 dbcContact={currentDbcUser} />
      ) : (
        <ContactDialog documentName="salesperson" createdBy={createdBy} />
      )}
    </S.MenuHeaderContainer>
  );
}
